import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil } from 'lucide-react';
import { usersApi, licenseApi, type User, type Role } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input, Select } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, Thead, Tbody, Th, Td, Tr } from '@/components/ui/table';
import { Dialog } from '@/components/ui/dialog';
import { Card, CardHeader, CardBody } from '@/components/ui/card';

const ROLE_COLOR: Record<Role, 'red' | 'blue' | 'slate'> = {
  admin: 'red', supervisor: 'blue', technician: 'slate',
};

interface CreateForm {
  email: string; password: string; full_name: string; role: Role;
}
interface EditForm {
  full_name: string; role: Role; is_active: boolean; password: string;
}

export function UsersPage() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [editUser, setEditUser] = useState<User | null>(null);
  const [createForm, setCreateForm] = useState<CreateForm>({
    email: '', password: '', full_name: '', role: 'technician',
  });
  const [editForm, setEditForm] = useState<EditForm>({
    full_name: '', role: 'technician', is_active: true, password: '',
  });

  const { data, isLoading } = useQuery({
    queryKey: ['users', page],
    queryFn: () => usersApi.list(page),
  });

  // License status — only used here to show the active-user cap when one
  // exists (on-prem Starter/Growth). SaaS and Enterprise have null cap
  // and the indicator hides.
  const { data: license } = useQuery({
    queryKey: ['license-status'],
    queryFn: () => licenseApi.status(),
  });
  const hasCap   = typeof license?.max_users === 'number';
  const atLimit  = hasCap && (license?.current_users ?? 0) >= (license?.max_users ?? 0);

  const create = useMutation({
    mutationFn: () => usersApi.create(createForm),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
      setCreateOpen(false);
      setCreateForm({ email: '', password: '', full_name: '', role: 'technician' });
    },
  });

  const update = useMutation({
    mutationFn: () =>
      usersApi.update(editUser!.id, {
        full_name: editForm.full_name,
        role: editForm.role,
        is_active: editForm.is_active,
        ...(editForm.password ? { password: editForm.password } : {}),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
      setEditUser(null);
    },
  });

  const openEdit = (u: User) => {
    setEditUser(u);
    setEditForm({ full_name: u.full_name, role: u.role, is_active: u.is_active, password: '' });
  };

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader
          title={
            hasCap
              ? `Users (${license?.current_users ?? 0}/${license?.max_users} on this license)`
              : `Users (${data?.pagination.total ?? 0})`
          }
          action={
            <Button
              size="sm"
              onClick={() => setCreateOpen(true)}
              disabled={atLimit}
              title={atLimit ? `License allows ${license?.max_users} active users. Deactivate one or upgrade.` : undefined}
            >
              <Plus size={14} /> Add User
            </Button>
          }
        />
        <CardBody className="p-0">
          {isLoading ? (
            <p className="px-5 py-8 text-center text-slate-400">Loading…</p>
          ) : (
            <Table>
              <Thead>
                <tr>
                  <Th>Name</Th><Th>Email</Th><Th>Role</Th>
                  <Th>Status</Th><Th>Created</Th><Th className="w-16">Edit</Th>
                </tr>
              </Thead>
              <Tbody>
                {data?.data.map(u => (
                  <Tr key={u.id}>
                    <Td className="font-medium">{u.full_name}</Td>
                    <Td className="text-xs text-slate-500">{u.email}</Td>
                    <Td><Badge color={ROLE_COLOR[u.role]}>{u.role}</Badge></Td>
                    <Td>
                      <Badge color={u.is_active ? 'green' : 'slate'}>
                        {u.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </Td>
                    <Td className="text-xs">{new Date(u.created_at).toLocaleDateString()}</Td>
                    <Td>
                      <button onClick={() => openEdit(u)} className="p-1 rounded hover:bg-slate-100 text-slate-500">
                        <Pencil size={14} />
                      </button>
                    </Td>
                  </Tr>
                ))}
                {data?.data.length === 0 && (
                  <Tr><Td className="py-8 text-center text-slate-400" colSpan={6}>No users found.</Td></Tr>
                )}
              </Tbody>
            </Table>
          )}
        </CardBody>
      </Card>

      {(data?.pagination.total ?? 0) > 50 && (
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={() => setPage(p => p - 1)} disabled={page === 1}>Prev</Button>
          <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={page * 50 >= (data?.pagination.total ?? 0)}>Next</Button>
        </div>
      )}

      {/* Create user dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} title="Add User">
        <form onSubmit={e => { e.preventDefault(); create.mutate(); }} className="flex flex-col gap-3">
          <Input label="Full Name *" value={createForm.full_name} onChange={e => setCreateForm(f => ({ ...f, full_name: e.target.value }))} required />
          <Input label="Email *" type="email" value={createForm.email} onChange={e => setCreateForm(f => ({ ...f, email: e.target.value }))} required />
          <Input label="Password *" type="password" value={createForm.password} onChange={e => setCreateForm(f => ({ ...f, password: e.target.value }))} required minLength={8} placeholder="Min 8 characters" />
          <Select label="Role *" value={createForm.role} onChange={e => setCreateForm(f => ({ ...f, role: e.target.value as Role }))}>
            <option value="technician">Technician</option>
            <option value="supervisor">Supervisor</option>
            <option value="admin">Admin</option>
          </Select>
          {create.error && <p className="text-sm text-red-600">{(create.error as Error).message}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button type="submit" disabled={create.isPending}>{create.isPending ? 'Creating…' : 'Create User'}</Button>
          </div>
        </form>
      </Dialog>

      {/* Edit user dialog */}
      <Dialog open={!!editUser} onClose={() => setEditUser(null)} title={`Edit — ${editUser?.full_name}`}>
        <form onSubmit={e => { e.preventDefault(); update.mutate(); }} className="flex flex-col gap-3">
          <Input label="Full Name *" value={editForm.full_name} onChange={e => setEditForm(f => ({ ...f, full_name: e.target.value }))} required />
          <Select label="Role *" value={editForm.role} onChange={e => setEditForm(f => ({ ...f, role: e.target.value as Role }))}>
            <option value="technician">Technician</option>
            <option value="supervisor">Supervisor</option>
            <option value="admin">Admin</option>
          </Select>
          <Select label="Status" value={editForm.is_active ? 'true' : 'false'} onChange={e => setEditForm(f => ({ ...f, is_active: e.target.value === 'true' }))}>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </Select>
          <Input label="New Password (leave blank to keep)" type="password" value={editForm.password} onChange={e => setEditForm(f => ({ ...f, password: e.target.value }))} minLength={8} placeholder="Optional — min 8 characters" />
          {update.error && <p className="text-sm text-red-600">{(update.error as Error).message}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={() => setEditUser(null)}>Cancel</Button>
            <Button type="submit" disabled={update.isPending}>{update.isPending ? 'Saving…' : 'Save Changes'}</Button>
          </div>
        </form>
      </Dialog>
    </div>
  );
}
